import Compressor from 'compressorjs';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { clearActiveReplyMessage } from '../actions/chatWindow/actions';
import { setHintQuestionsHidden } from '../actions/chatHintQuestions';
import { sendFileMessage } from '../actions/sendFileMessage';
import { selectActiveReplyMessage, currentOpenChatIDSelector } from '../selectors';
import { EFileError } from '../types/alert';
import { TFileTypes } from '../types/messageFiles';
import { getMessageFileSendType } from '../utils/getMessageFileSendType';

const SIZE_LIMIT = 50000000; // 50MB
const COMPRESS_SIZE_LIMIT = 3000000; // 3MB

type TUseFileChange = {
  documentMimeTypes: string[];
  imageMimeTypes: string[];
  inputSendType?: TFileTypes | null;
  handleToggleErrorModal: (text?: EFileError) => void;
  handleToggleAttachFileModal: () => void;
};

export const useFileChange = ({
  documentMimeTypes,
  imageMimeTypes,
  inputSendType,
  handleToggleErrorModal,
  handleToggleAttachFileModal,
}: TUseFileChange) => {
  const dispatch = useDispatch();

  const chatId = useSelector(currentOpenChatIDSelector) || '';
  const activeReplyMessage = useSelector(selectActiveReplyMessage);

  const onFileChange = React.useCallback(
    (event: React.ChangeEvent) => {
      const target = event.target as HTMLInputElement;

      if (target.files) {
        Array.from(target.files).map(file => {
          // При выборе отправить как "Документ" определяем тип отправляемого файла в зависимости от расширения
          const sendType = inputSendType || getMessageFileSendType(imageMimeTypes, file.type);

          if (file.size > SIZE_LIMIT) {
            handleToggleErrorModal(EFileError.Size);

            return;
          }

          if (!documentMimeTypes.includes(file.type)) {
            handleToggleErrorModal(EFileError.Type);

            return;
          }

          // Если фотофайл превышает 3мб - сжимаем
          if (sendType === 'imageAsDocument' && file.size > COMPRESS_SIZE_LIMIT) {
            new Compressor(file, {
              convertSize: COMPRESS_SIZE_LIMIT,
              convertTypes: imageMimeTypes,
              success(result) {
                const fileName = 'name' in result ? result?.name : 'file';

                const compressedFile = new File([result], fileName);

                dispatch(
                  sendFileMessage({
                    file: compressedFile,
                    chatId,
                    type: sendType,
                    reply: activeReplyMessage,
                    failCallback: handleToggleErrorModal,
                  }),
                );
                handleToggleAttachFileModal();
                dispatch(setHintQuestionsHidden({ chatId }));
                dispatch(clearActiveReplyMessage());
              },
              error() {
                handleToggleErrorModal();
              },
            });

            return;
          }

          dispatch(
            sendFileMessage({
              file,
              chatId,
              type: sendType,
              reply: activeReplyMessage,
              failCallback: handleToggleErrorModal,
            }),
          );
          handleToggleAttachFileModal();
          dispatch(setHintQuestionsHidden({ chatId }));
          dispatch(clearActiveReplyMessage());
        });
      }
    },
    [
      chatId,
      dispatch,
      documentMimeTypes,
      handleToggleAttachFileModal,
      handleToggleErrorModal,
      imageMimeTypes,
      inputSendType,
      activeReplyMessage,
    ],
  );

  return { onFileChange };
};
